import React, { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react-lite";
import {
  CTable,
  CContainer,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CProgress,
  CProgressBar,
  CCol,
  CButton,
  CSpinner,
  CAlert,
} from "@coreui/react";
import waitersStore from "../../store/WaitersStore";
import settingsStore from "../../store/SettingsStore";
import DashboardFilters from "../UI/PeriodFilters/DashboardFilters";
import WaiterDetailModal from "./WaiterDetailModal";
import WaiterInsightPanel from "../Insights/WaiterInsightPanel";
import { formatYmd } from "../../utils/dateRange";
import { logReportView } from "../../api/reportLogApi";

const WaitersStats = observer(() => {
  const [selectedServer, setSelectedServer] = useState("");
  const [quickRange, setQuickRange] = useState("week");
  const [detailWaiter, setDetailWaiter] = useState(null);
  // The period actually applied (button/quick-range), decoupled from the live date
  // pickers — the AI panel below must only re-fetch on a real period change, not on
  // every date the user clicks while browsing (GigaChat calls cost tokens).
  const [appliedRange, setAppliedRange] = useState({ from: null, to: null });

  const load = useCallback(
    (from, to) => {
      if (!selectedServer || !from || !to) return;
      waitersStore.setFromDate(from);
      waitersStore.setToDate(to);
      waitersStore.getWaiters(selectedServer, from, to);
      setAppliedRange({ from, to });
    },
    [selectedServer],
  );

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      const today = new Date();
      const past = new Date(today);
      past.setDate(today.getDate() - 7);
      waitersStore.setFromDate(formatYmd(past));
      waitersStore.setToDate(formatYmd(today));
      logReportView({
        event: "report_view",
        reportKey: "waiters",
        reportTitle: "Статистика официантов",
        userId,
        path: "/waiters",
      });
    };
    init();
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (selectedServer === "") {
      setSelectedServer(String(settingsStore.servers[0].id));
    }
  }, [settingsStore.servers, selectedServer]);

  useEffect(() => {
    if (!selectedServer || !waitersStore.fromDate || !waitersStore.toDate) return;
    waitersStore.getWaiters(selectedServer, waitersStore.fromDate, waitersStore.toDate);
    setAppliedRange({ from: waitersStore.fromDate, to: waitersStore.toDate });
  }, [selectedServer, waitersStore.groupByRestaurant]);

  const handleGroupByChange = (byRestaurant) => {
    waitersStore.setGroupByRestaurant(byRestaurant);
    if (!selectedServer || !waitersStore.fromDate || !waitersStore.toDate) return;
    waitersStore.getWaiters(selectedServer, waitersStore.fromDate, waitersStore.toDate);
    setAppliedRange({ from: waitersStore.fromDate, to: waitersStore.toDate });
  };

  const handleRestaurantGroupChange = (groupName) => {
    waitersStore.setSelectedRestaurantGroup(groupName);
    if (!selectedServer || !waitersStore.fromDate || !waitersStore.toDate) return;
    waitersStore.getWaiters(selectedServer, waitersStore.fromDate, waitersStore.toDate);
    setAppliedRange({ from: waitersStore.fromDate, to: waitersStore.toDate });
  };

  const overallTotal = Number(waitersStore.overallTotalSum) || 0;
  const byRestaurant = waitersStore.byRestaurant;

  const openDetail = (waiter) => {
    setDetailWaiter({
      name: waiter.waiterName,
      datas: waitersStore.getWaiterDailyData(
        waiter.waiterName,
        byRestaurant ? waitersStore.selectedRestaurantGroup : null,
      ),
    });
  };

  return (
    <CContainer style={{ flex: "1", marginTop: "150px" }}>
      <h4 className="mb-3">Статистика официантов</h4>
      <DashboardFilters
        servers={settingsStore.servers}
        selectedServer={selectedServer}
        onServerChange={(e) => setSelectedServer(e.target.value)}
        fromDate={waitersStore.fromDate}
        toDate={waitersStore.toDate}
        onFromChange={(d) => waitersStore.setFromDate(d)}
        onToChange={(d) => waitersStore.setToDate(d)}
        onRefresh={load}
        quickRange={quickRange}
        onQuickRangeChange={setQuickRange}
        showGroupBy
        groupByRestaurant={waitersStore.groupByRestaurant}
        onGroupByChange={handleGroupByChange}
        restaurantGroups={waitersStore.restaurantGroups}
        selectedRestaurantGroup={waitersStore.selectedRestaurantGroup}
        onRestaurantGroupChange={handleRestaurantGroupChange}
        disabled={waitersStore.isLoading}
        serverSelectId="waitersServerSelect"
      />
      {waitersStore.isError && (
        <CAlert color="danger" className="mb-3">
          Не удалось загрузить данные по официантам
        </CAlert>
      )}
      <CCol className="mb-3">
        <p className="mb-0">
          {byRestaurant
            ? `Выручка по официантам (${waitersStore.selectedRestaurantGroup || "группа"}) за ${waitersStore.fromDate} — ${waitersStore.toDate}`
            : `Выручка по официантам за ${waitersStore.fromDate} — ${waitersStore.toDate}`}
        </p>
      </CCol>
      {waitersStore.isLoading && (
        <div className="text-center py-3">
          <CSpinner />
        </div>
      )}
      <CTable hover responsive small>
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell scope="col">Официант</CTableHeaderCell>
            {byRestaurant && <CTableHeaderCell scope="col">Группа</CTableHeaderCell>}
            <CTableHeaderCell scope="col">Выручка</CTableHeaderCell>
            <CTableHeaderCell scope="col">
              % от общей выручки ({overallTotal} &#8381;)
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" />
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {waitersStore.waiters.map((waiter, idx) => {
            const sum = waiter.totalSum;
            const sharePct = overallTotal ? (100 * sum) / overallTotal : 0;
            const rowKey = `${waiter.waiterName}-${idx}`;
            return (
              <CTableRow key={rowKey} style={{ cursor: "pointer" }} onClick={() => openDetail(waiter)}>
                <CTableDataCell>{waiter.waiterName}</CTableDataCell>
                {byRestaurant && <CTableDataCell>{waiter.restaurantGroup}</CTableDataCell>}
                <CTableDataCell>{sum} &#8381;</CTableDataCell>
                <CTableDataCell>
                  <CProgress color={sharePct > 50 ? "success" : "info"} value={sharePct}>
                    <CProgressBar>{Math.ceil(sharePct)} %</CProgressBar>
                  </CProgress>
                </CTableDataCell>
                <CTableDataCell>
                  <CButton size="sm" color="primary" variant="outline" onClick={(e) => { e.stopPropagation(); openDetail(waiter); }}>
                    По дням
                  </CButton>
                </CTableDataCell>
              </CTableRow>
            );
          })}
        </CTableBody>
      </CTable>
      <WaiterDetailModal
        waiterName={detailWaiter?.name}
        datas={detailWaiter?.datas ?? []}
        onClose={() => setDetailWaiter(null)}
      />
      <WaiterInsightPanel
        serverId={selectedServer}
        dateFrom={appliedRange.from}
        dateTo={appliedRange.to}
        groupByRestaurant={waitersStore.groupByRestaurant}
      />
    </CContainer>
  );
});

export default WaitersStats;
